import { Fragment } from "react";
import { Outlet, useOutletContext, useParams } from "react-router-dom";

import { ApiError } from "../api/client";
import { useTraderDetail } from "../api/queries";
import type { TraderDetail } from "../api/types";
import "./pages.css";
import { useSession } from "../auth";
import { AdminPanel } from "../components/AdminPanel";
import { ErrorBand } from "../components/ErrorBand";
import { Skeleton } from "../components/Skeleton";
import { Tabs } from "../components/Tabs";
import { TitleBand } from "../components/TitleBand";
import { aged, plural, when } from "../format";

/** The two tabs read the same fetch. Splitting them into two requests would
 *  refetch a trader's whole window every time somebody switched view. */
export function useTrader(): TraderDetail {
  return useOutletContext<TraderDetail>();
}

/** How old each side of the table is, spelled per mode.
 *
 *  One line for the page would have to pick a time and be wrong about the
 *  other side: a buy-only walk refreshes the bids and leaves the asks standing
 *  at their own age. That mismatch is exactly what this page was rebuilt to
 *  stop hiding, so it is stated rather than averaged. */
function AsOf({ asOf }: { asOf: Record<string, number> }) {
  const sides: Record<string, string> = { buy: "bids", sell: "asks", craft: "fees" };
  const entries = Object.entries(asOf).filter(([mode]) => mode !== "package");
  if (entries.length === 0) return null;
  return (
    <p className="scan-meta">
      {/* The middot is a flex sibling, not a character inside the entry, so
          .scan-meta's gap spaces it the way it spaces the first-seen line
          above — one convention on one line, not two. */}
      {entries.map(([mode, at], i) => (
        <Fragment key={mode}>
          {i > 0 ? <span aria-hidden="true">·</span> : null}
          <span title={when(at)}>{sides[mode] ?? mode} as of {aged(at)}</span>
        </Fragment>
      ))}
    </p>
  );
}

export function Trader() {
  const { nick = "" } = useParams();
  const { me } = useSession();
  const detail = useTraderDetail(nick);

  if (detail.isError) {
    const missing = detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <>
        <TitleBand title={nick || "Trader"} />
        <ErrorBand
          message={missing ? "No trader by that name." : "Could not load this trader."}
          hint={missing
            ? "The nick may be spelled differently in a later scan."
            : "The server may be restarting. Reload in a moment."}
        />
      </>
    );
  }
  if (!detail.data) {
    return <><TitleBand title={nick || "Trader"} /><Skeleton rows={8} /></>;
  }

  const trader = detail.data;
  const base = `/traders/${encodeURIComponent(trader.nick)}`;

  return (
    <>
      <TitleBand
        title={trader.nick}
        note={`${trader.offers} offers across ${trader.scans} scans`}
      />
      <p className="scan-meta">
        <span>first seen {when(trader.first_seen)}</span>
        <span aria-hidden="true">·</span>
        <span>last seen {when(trader.last_seen)}</span>
      </p>
      <AsOf asOf={trader.now_as_of} />
      {/* Somebody looked and they were not there. Without this a stale table
          reads as a live one to anyone who does not know the scan cadence —
          and a zero here means nobody has walked since, which is a different
          thing and must not be reported as having packed up. */}
      {trader.missed.count > 0 ? (
        <p className="scan-meta dim">
          not seen in the last {plural(trader.missed.count, "walk")} of{" "}
          {trader.history[0]?.city || "their town"}
        </p>
      ) : null}

      <Tabs tabs={[
        { to: base, label: "Now", end: true },
        { to: `${base}/history`, label: "History" },
      ]} />

      <Outlet context={trader} />

      {me?.role === "owner" ? (
        <AdminPanel kind="traders" id={trader.trader_id} />
      ) : null}
    </>
  );
}
